import React, { useEffect, useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CustomerSelector } from "../../../components/utils/moduleSelectors/customerSelector";
import { ContactsSelector } from "../../../components/utils/moduleSelectors/contactSelector";
import { ReportSelector } from "../../../components/utils/moduleSelectors/reportSelector";
import { ProjectSelector } from "../../../components/utils/moduleSelectors/projectSelector";
import { CaseSelector } from "../../../components/utils/moduleSelectors/caseSelector";
import { PMDialog } from "../../../components/utils/PMDialog";
import { planGhostBtnClass } from "../../../components/dashboard/projects/projectPlanTheme";

const SELECTOR_Z = 2600;
const MENU_Z = 2500;

const MODULE_COLORS = {
  report: "#60a5fa",
  contact: "#34d399",
  customer: "#f87171",
  project: "#fbbf24",
  case: "#a78bfa",
};

export function moduleTagsFromTask(task) {
  const tags = (task?.module_tags || []).map(normalizeModuleTag);
  const seen = new Set(tags.map((row) => tagKey(row)));

  const addLegacy = (module, obj) => {
    if (!obj?.id) return;
    const key = tagKey({ module, module_param: obj.id });
    if (seen.has(key)) return;
    tags.push({ module, module_param: obj.id, object: obj });
    seen.add(key);
  };

  addLegacy("customer", task?.customer);
  addLegacy("project", task?.project);
  if (task?.case?.id) addLegacy("case", task.case);

  return tags;
}

export function moduleTagsPayload(tags) {
  return (tags || []).map((row) => ({
    module: row.module,
    module_param: Number(row.module_param),
  }));
}

function normalizeModuleTag(row) {
  return {
    id: row.id,
    module: row.module,
    module_param: row.module_param ?? row.moduleParam,
    object: row.object,
  };
}

function tagKey(row) {
  return `${row.module}:${row.module_param}`;
}

function tagLabel(row, t) {
  const obj = row.object;
  if (obj?.displayTitle) return obj.displayTitle;
  if (obj?.name) return obj.name;
  if (obj?.title) return obj.title;
  if (obj?.case_number) return `#${obj.case_number}`;
  const mod = row.module ? t(row.module) : row.module;
  return `${mod} #${row.module_param}`;
}

function tagPath(row) {
  const id = row.module_param;
  if (!id) return null;
  switch (row.module) {
    case "case":
      return `/dashboard/cases/${id}`;
    case "project":
      return `/dashboard/projects/${id}`;
    case "customer":
      return `/dashboard/customers/${id}`;
    case "report":
      return `/dashboard/service/reports/${id}`;
    default:
      return null;
  }
}

function TodoModuleTag({ row, readOnly, onRemove, t }) {
  const navigate = useNavigate();
  const label = tagLabel(row, t);
  const color = MODULE_COLORS[row.module] || "var(--td-text-3)";
  const path = tagPath(row);

  return (
    <span className="td-module-tag" style={{ borderColor: color, color }}>
      {path ? (
        <button type="button" className="td-module-tag-link" onClick={() => navigate(path)}>
          {label}
        </button>
      ) : (
        <span>{label}</span>
      )}
      {!readOnly && onRemove ? (
        <button type="button" className="td-module-tag-remove" onClick={onRemove} aria-label={t("Remove")}>
          <XMarkIcon className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </span>
  );
}

/**
 * Multi-module links for tasks (customer, project, contact, report, case) — same pattern as case items.
 */
export function TodoModuleTagField({ value = [], onChange, readOnly = false, compact = false }) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openModuleDialog, setOpenModuleDialog] = useState(null);
  const tags = useMemo(() => (Array.isArray(value) ? value.map(normalizeModuleTag) : []), [value]);

  const moduleOptions = useMemo(
    () => [
      { key: "report", label: t("Report") },
      { key: "contact", label: t("Contact") },
      { key: "customer", label: t("Customer") },
      { key: "project", label: t("Project") },
      { key: "case", label: t("Case") },
    ],
    [t]
  );

  const openSelector = (type) => {
    setMenuOpen(false);
    setOpenModuleDialog(null);
    window.setTimeout(() => setOpenModuleDialog(type), 50);
  };

  const handleSelectorClose = () => {
    setOpenModuleDialog(null);
  };

  const addTag = (type, event) => {
    const param = event?.target?.value;
    if (param === null || param === undefined || param === "") return;

    const data = event?.target?.data;
    const payload = normalizeModuleTag({
      module: type,
      module_param: param,
      object: data?.object ?? data,
    });
    const key = tagKey(payload);
    if (tags.some((row) => tagKey(row) === key)) {
      setOpenModuleDialog(null);
      return;
    }
    onChange([...tags, payload]);
    setOpenModuleDialog(null);
  };

  const removeTag = (index) => {
    const next = [...tags];
    next.splice(index, 1);
    onChange(next);
  };

  useEffect(() => {
    if (readOnly) {
      setMenuOpen(false);
      setOpenModuleDialog(null);
    }
  }, [readOnly]);

  return (
    <>
      <div className={`td-module-tags ${compact ? "is-compact" : ""}`.trim()}>
        {tags.map((row, idx) => (
          <TodoModuleTag
            key={tagKey(row)}
            row={row}
            t={t}
            readOnly={readOnly}
            onRemove={readOnly ? null : () => removeTag(idx)}
          />
        ))}
        {!readOnly ? (
          <button
            type="button"
            className={`${planGhostBtnClass} td-chip-btn td-module-tag-add`}
            onClick={() => setMenuOpen(true)}
          >
            <span>+</span>
            <span>{t("todo.moduleTagsAdd")}</span>
          </button>
        ) : null}
        {!readOnly && tags.length === 0 ? (
          <span className="td-prop-value-empty">—</span>
        ) : null}
      </div>

      <PMDialog
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        title={t("todo.moduleTagsAdd")}
        size="sm"
        dense
        zIndex={MENU_Z}
      >
        <div className="td-module-tag-menu-dialog">
          {moduleOptions.map((item) => (
            <button
              key={item.key}
              type="button"
              className="td-module-tag-menu-item"
              onClick={() => openSelector(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </PMDialog>

      <ContactsSelector
        showField={false}
        open={openModuleDialog === "contact"}
        onClose={handleSelectorClose}
        onChange={(data) => addTag("contact", data)}
        zIndex={SELECTOR_Z}
      />
      <CustomerSelector
        showField={false}
        open={openModuleDialog === "customer"}
        onClose={handleSelectorClose}
        onChange={(data) => addTag("customer", data)}
        zIndex={SELECTOR_Z}
      />
      <ReportSelector
        showField={false}
        open={openModuleDialog === "report"}
        onModalClose={handleSelectorClose}
        onChange={(data) => addTag("report", data)}
      />
      <ProjectSelector
        showField={false}
        open={openModuleDialog === "project"}
        onClose={handleSelectorClose}
        onChange={(data) => addTag("project", data)}
        zIndex={SELECTOR_Z}
      />
      <CaseSelector
        showField={false}
        open={openModuleDialog === "case"}
        onClose={handleSelectorClose}
        onChange={(data) => addTag("case", data)}
        zIndex={SELECTOR_Z}
      />
    </>
  );
}
